import React from "react";
import { cn } from "@/lib/utils";
import { highlightKeywordMatches } from "@/lib/highlight-keywords";
import { FlagReason } from "@/lib/interaction-types";

// Matches standalone integers and score-style tokens (3A, 4B, 5A, 5B, etc.).
// Uses exec() loop rather than split() to avoid V8 edge cases with \b + optional groups.
// No /i flag: score codes are uppercase; this also prevents greedy consumption of
// lowercase letters (e.g. the "m" in "15 minutes") from breaking the \b assertion.
function highlightNumbers(text: string): React.ReactNode[] {
  const result: React.ReactNode[] = [];
  const regex = /\b\d+[A-Z]?\b/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      result.push(text.slice(lastIndex, match.index));
    }
    result.push(
      <span
        key={match.index}
        className="bg-blue-100 dark:bg-blue-900/30 text-blue-900 dark:text-blue-200 rounded px-0.5 font-medium"
      >
        {match[0]}
      </span>
    );
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) {
    result.push(text.slice(lastIndex));
  }
  return result;
}

// Applies keyword highlights first, then number highlights within any
// remaining plain-string segments (only when showNumberHighlights is true).
// The two highlight types never conflict.
function applyAllHighlights(
  line: string,
  keywordMatches: string[],
  showNumberHighlights: boolean
): React.ReactNode[] {
  const withKeywords = highlightKeywordMatches(line, keywordMatches);
  if (!showNumberHighlights) {
    return withKeywords;
  }
  return withKeywords.flatMap((node) =>
    typeof node === "string" ? highlightNumbers(node) : [node]
  );
}

interface InteractionNoteRendererProps {
  note: string | null;
  flagReasons?: FlagReason[] | null;
  className?: string;
  showNumberHighlights?: boolean;
}

export function InteractionNoteRenderer({
  note,
  flagReasons,
  className,
  showNumberHighlights,
}: InteractionNoteRendererProps) {
  if (!note || !note.trim()) return null;

  const paragraphs = note
    .split(/\n\n+/)
    .map((p) => p.trim())
    .filter(Boolean);

  const keywordMatches =
    flagReasons?.find((r) => r.type === "keyword")?.matches ?? [];

  return (
    <div className={cn("space-y-4", className)}>
      {paragraphs.map((para, i) => {
        const lines = para.split("\n");
        return (
          <p key={i} className="text-sm">
            {lines.map((line, j) => (
              <React.Fragment key={j}>
                {applyAllHighlights(line, keywordMatches, !!showNumberHighlights)}
                {j < lines.length - 1 && <br />}
              </React.Fragment>
            ))}
          </p>
        );
      })}
    </div>
  );
}
